import { Extensions, Node } from "@tiptap/core";
import { Highlight } from "@tiptap/extension-highlight";
import { Image } from "@tiptap/extension-image";
import { MentionOptions } from "@tiptap/extension-mention";
import { Subscript } from "@tiptap/extension-subscript";
import { Superscript } from "@tiptap/extension-superscript";
import Table from "@tiptap/extension-table";
import { TableCell } from "@tiptap/extension-table-cell";
import { TableHeader } from "@tiptap/extension-table-header";
import { TableRow } from "@tiptap/extension-table-row";
import { TextAlign } from "@tiptap/extension-text-align";
import TextStyle from "@tiptap/extension-text-style";
import { Underline } from "@tiptap/extension-underline";
import { JSONContent, useEditor } from "@tiptap/react";
import { StarterKit } from "@tiptap/starter-kit";
import React from "react";
import "prosemirror-image-plugin/dist/styles/common.css";
import "prosemirror-image-plugin/dist/styles/sideResize.css";
import "prosemirror-image-plugin/dist/styles/withResize.css";

import { DocumentFontSizeControl } from "~/components/editor/document-font-size-control";
import { DocumentFontSize } from "~/components/editor/document-font-size-extension";
import { ImageUploadControl, ImageUploader, startImageUpload } from "~/components/editor/ImageUploader";
import { TableActionsControl, TableInsertControl } from "~/components/editor/table";
import { Input, Link, RichTextEditor } from "~/ui";
import { proxyApi } from "~/utils/api";
import { EditorSchemaType } from "~/utils/types/rich-text";

import styles from "./Editor.module.css";

type EditorFeatureProps = {
  enableImages?: boolean;
  enableLinks?: boolean;
  enableSubscriptAndSuperscript?: boolean;
  enableTables?: boolean;
  /** Document boilerplate: Normal / Small (0.65rem) — PDF via sanitizeEditorHtmlForPdf. */
  enableDocumentFontSizes?: boolean;
};

type EditorProps = EditorFeatureProps & {
  disabled?: boolean;
  value?: EditorSchemaType;
  onChange?: (value: EditorSchemaType) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  error?: React.ReactNode;
  mention?: Node<MentionOptions>;
  defaultContent?: string;
  /** ProseMirror editing area min height (default 300px). */
  contentMinHeight?: string | number;
};
export const Editor = (props: EditorProps) => {
  const enableSubscriptAndSuperscript = props.enableSubscriptAndSuperscript ?? true;
  const imageUploader = ImageUploader.configure({
    hasTitle: false,
    isBlock: false,
    enableResize: true,
    createOverlay: () => undefined,
    uploadFile: async file => {
      const create = await proxyApi.files.createUpload.mutate({ fileName: file.name });
      const upload = await fetch(create.signedUrl, { method: "PUT", body: file, headers: { "Content-Type": file.type } });
      return upload.ok ? create.url : "";
    },
  });

  const extensions: Extensions = [StarterKit, Underline, Highlight, TextAlign.configure({ types: ["heading", "paragraph"] })];
  if (enableSubscriptAndSuperscript) {
    extensions.push(Superscript, Subscript);
  }
  if (props.mention) {
    extensions.push(props.mention);
  }
  if (props.enableImages) {
    extensions.push(Image, imageUploader);
  }
  if (props.enableLinks) {
    extensions.push(Link);
  }
  if (props.enableDocumentFontSizes) {
    extensions.push(TextStyle, DocumentFontSize);
  }
  if (props.enableTables) {
    extensions.push(
      Table.configure({
        resizable: true,
      }),
      TableRow,
      TableHeader,
      TableCell,
    );
  }

  const resolvedContent: string | JSONContent = props.defaultContent?.trim().length
    ? props.defaultContent
    : ((props.value?.json as JSONContent | undefined) ?? "<p></p>");

  const handleFocus = () => props.onFocus?.();
  const handleBlur = () => props.onBlur?.();

  const editor = useEditor({
    editable: props.disabled !== true,
    extensions,
    content: resolvedContent,
    onFocus: handleFocus,
    onBlur: handleBlur,
    onUpdate: ({ editor }) => {
      props.onChange?.({
        json: editor.getJSON(),
        html: editor.getHTML(),
        text: editor.getText().toLowerCase(),
      });
    },
  });

  const onImageUploadChange = (file: File | null) => {
    if (file == null || editor?.view == undefined) {
      return;
    }

    startImageUpload(editor?.view, file, file.name, imageUploader.options, editor.schema);
  };

  if (editor != null && props.value != undefined && props.value["text"] === undefined) {
    props.onChange?.({
      json: editor.getJSON(),
      html: editor.getHTML(),
      text: editor.getText().toLowerCase(),
    });
  }

  const renderImageAndTableToolbar = !!props.enableImages || !!props.enableTables;
  const contentMinHeight = props.contentMinHeight ?? "300px";

  return (
    <>
      {editor && (
        <Input.Wrapper error={props.error}>
          <RichTextEditor editor={editor} style={{ borderColor: props.error !== undefined ? "#EF4444" : "" }}>
            {!props.disabled && (
              <>
                <RichTextEditor.Toolbar>
                  <RichTextEditor.ControlsGroup>
                    <RichTextEditor.Bold />
                    <RichTextEditor.Italic />
                    <RichTextEditor.Underline />
                    <RichTextEditor.Strikethrough />
                    <RichTextEditor.ClearFormatting />
                    <RichTextEditor.Highlight />
                    <RichTextEditor.Code />
                  </RichTextEditor.ControlsGroup>
                  <RichTextEditor.ControlsGroup>
                    <RichTextEditor.H1 />
                    <RichTextEditor.H2 />
                    <RichTextEditor.H3 />
                    <RichTextEditor.H4 />
                  </RichTextEditor.ControlsGroup>
                  <RichTextEditor.ControlsGroup>
                    <RichTextEditor.Blockquote />
                    <RichTextEditor.Hr />
                    <RichTextEditor.BulletList />
                    <RichTextEditor.OrderedList />
                    {enableSubscriptAndSuperscript ? (
                      <>
                        <RichTextEditor.Subscript />
                        <RichTextEditor.Superscript />
                      </>
                    ) : null}
                  </RichTextEditor.ControlsGroup>
                  {props.enableLinks && (
                    <RichTextEditor.ControlsGroup>
                      <RichTextEditor.Link />
                      <RichTextEditor.Unlink />
                    </RichTextEditor.ControlsGroup>
                  )}
                  <RichTextEditor.ControlsGroup>
                    <RichTextEditor.AlignLeft />
                    <RichTextEditor.AlignCenter />
                    <RichTextEditor.AlignJustify />
                    <RichTextEditor.AlignRight />
                  </RichTextEditor.ControlsGroup>
                  {props.enableDocumentFontSizes ? (
                    <RichTextEditor.ControlsGroup>
                      <DocumentFontSizeControl />
                    </RichTextEditor.ControlsGroup>
                  ) : null}
                </RichTextEditor.Toolbar>
                {renderImageAndTableToolbar && (
                  <RichTextEditor.Toolbar>
                    {props.enableTables && (
                      <RichTextEditor.ControlsGroup>
                        <TableInsertControl />
                        <TableActionsControl />
                      </RichTextEditor.ControlsGroup>
                    )}
                    {props.enableImages && (
                      <RichTextEditor.ControlsGroup>
                        <ImageUploadControl onChange={onImageUploadChange} />
                      </RichTextEditor.ControlsGroup>
                    )}
                  </RichTextEditor.Toolbar>
                )}
              </>
            )}
            <RichTextEditor.Content className={styles.content} style={{ minHeight: contentMinHeight }} />
          </RichTextEditor>
        </Input.Wrapper>
      )}
    </>
  );
};

type RichTextViewProps = Pick<EditorProps, "value">;
export const RichTextView = (props: RichTextViewProps) => {
  return <div dangerouslySetInnerHTML={{ __html: props?.value?.html ?? "" }} />;
};
